class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    window.setTimeout(() => {
      this.classList.add('animate', 'active');
      initializeFigsSmartCart(this);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus === 'function'
        ) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const content = cartDrawerNote.nextElementSibling;

    if (content && content.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', content.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const details = event.currentTarget.closest('details');

      event.currentTarget.setAttribute(
        'aria-expanded',
        details ? String(!details.hasAttribute('open')) : 'false'
      );
    });

    if (
      cartDrawerNote.parentElement &&
      typeof onKeyUpEscape === 'function'
    ) {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHTML) {
        return;
      }

      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    });

    window.setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      initializeFigsSmartCart(this);
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const sectionElement = parsedDocument.querySelector(selector);

    return sectionElement ? sectionElement.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

/* ==================================================
   FIGS.MA — SMART RECOMMENDATIONS V3
   One compact card at a time, expandable options,
   variant image updates and direct Ajax add.
================================================== */

const FIGS_SMART_CARD_SELECTOR = '[data-figs-smart-card]';

function getFigsVariantData(card) {
  if (!card) return [];

  if (Array.isArray(card.figsVariants)) {
    return card.figsVariants;
  }

  const dataElement = card.querySelector('[data-figs-variant-data]');

  if (!dataElement) {
    card.figsVariants = [];
    return card.figsVariants;
  }

  try {
    const parsed = JSON.parse(dataElement.textContent.trim());

    card.figsVariants = Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('FIGS Smart Cart: invalid variant data', error);
    card.figsVariants = [];
  }

  return card.figsVariants;
}

function getFigsOptionSelects(card) {
  return Array.from(
    card.querySelectorAll('[data-figs-option-select]')
  ).sort(
    (first, second) =>
      Number(first.dataset.optionIndex || 0) -
      Number(second.dataset.optionIndex || 0)
  );
}

function isFigsSizeSelect(select) {
  const optionName = String(select?.dataset.optionName || '').toLowerCase();

  return optionName.includes('taille') || optionName.includes('size');
}

function getFigsSelectedValues(card) {
  return getFigsOptionSelects(card).map((select) => select.value);
}

function findFigsExactVariant(variants, selectedValues) {
  return variants.find(
    (variant) =>
      variant.available &&
      variant.options.every(
        (value, index) => String(value) === String(selectedValues[index])
      )
  );
}

function findFigsBestVariant(card, changedIndex = null) {
  const variants = getFigsVariantData(card).filter(
    (variant) => variant.available
  );

  if (!variants.length) return null;

  const selectedValues = getFigsSelectedValues(card);
  const exactVariant = findFigsExactVariant(variants, selectedValues);

  if (exactVariant) {
    return exactVariant;
  }

  let candidates = variants;

  if (
    changedIndex !== null &&
    typeof selectedValues[changedIndex] !== 'undefined'
  ) {
    const changedValue = selectedValues[changedIndex];

    const matchingChangedValue = variants.filter(
      (variant) =>
        String(variant.options[changedIndex]) === String(changedValue)
    );

    if (matchingChangedValue.length) {
      candidates = matchingChangedValue;
    }
  }

  return candidates
    .map((variant) => {
      const score = variant.options.reduce((total, value, index) => {
        if (index === changedIndex) return total;

        return total + (
          String(value) === String(selectedValues[index]) ? 1 : 0
        );
      }, 0);

      return { variant, score };
    })
    .sort((first, second) => second.score - first.score)[0]?.variant || null;
}

function updateFigsOptionAvailability(card, selectedVariant) {
  if (!card || !selectedVariant) return;

  const variants = getFigsVariantData(card).filter(
    (variant) => variant.available
  );

  const selects = getFigsOptionSelects(card);
  const selectedValues = selects.map((select) => select.value);

  selects.forEach((select, optionIndex) => {
    const restrictByOtherOptions = isFigsSizeSelect(select);

    Array.from(select.options).forEach((option) => {
      const optionValue = option.value;

      const isAvailable = variants.some((variant) => {
        if (String(variant.options[optionIndex]) !== String(optionValue)) {
          return false;
        }

        if (!restrictByOtherOptions) {
          return true;
        }

        return selects.every((otherSelect, otherIndex) => {
          if (otherIndex === optionIndex) return true;

          return (
            String(variant.options[otherIndex]) ===
            String(selectedValues[otherIndex])
          );
        });
      });

      option.disabled = !isAvailable;
    });
  });
}

function updateFigsCardLinks(card, variantId) {
  const productUrl = card.dataset.productUrl;

  if (!productUrl || !variantId) return;

  const separator = productUrl.includes('?') ? '&' : '?';
  const variantUrl = `${productUrl}${separator}variant=${variantId}`;

  card.querySelectorAll('[data-figs-card-link]').forEach((link) => {
    link.href = variantUrl;
  });
}

function updateFigsCardImage(card, variant) {
  const image = card.querySelector('[data-figs-card-image]');

  if (!image || !variant?.image || image.src === variant.image) {
    return;
  }

  card.classList.add('is-image-changing');

  const preloader = new Image();

  preloader.onload = () => {
    if (!document.body.contains(card)) return;

    image.src = variant.image;

    if (variant.imageAlt) {
      image.alt = variant.imageAlt;
    }

    card.classList.remove('is-image-changing');
  };

  preloader.onerror = () => {
    card.classList.remove('is-image-changing');
  };

  preloader.src = variant.image;
}

function applyFigsVariantToCard(card, variant) {
  if (!card || !variant) return;

  const selects = getFigsOptionSelects(card);

  selects.forEach((select, index) => {
    const value = variant.options[index];

    if (
      typeof value !== 'undefined' &&
      Array.from(select.options).some(
        (option) => String(option.value) === String(value)
      )
    ) {
      select.value = value;
    }
  });

  card.dataset.selectedVariantId = String(variant.id);

  const price = card.querySelector('[data-figs-card-price]');

  if (price && variant.price) {
    price.textContent = variant.price;
  }

  const addButton = card.querySelector(
    '[data-figs-config] [data-figs-quick-add]'
  );

  if (addButton) {
    addButton.disabled = !variant.available;
  }

  updateFigsOptionAvailability(card, variant);
  updateFigsCardImage(card, variant);
  updateFigsCardLinks(card, variant.id);
}

function syncFigsRecommendationCard(card, changedIndex = null) {
  if (!card) return;

  const variants = getFigsVariantData(card);
  const configuredSelects = getFigsOptionSelects(card);

  if (!configuredSelects.length) {
    const directButton = card.querySelector(
      '[data-figs-quick-add][data-variant-id]'
    );

    if (directButton) {
      card.dataset.selectedVariantId = directButton.dataset.variantId;
    }

    return;
  }

  if (!variants.length) {
    card.classList.add('has-error');

    const error = card.querySelector('[data-figs-card-error]');

    if (error) {
      error.textContent =
        'Les options de cet article ne sont pas disponibles.';
    }

    return;
  }

  const selectedVariant = findFigsBestVariant(card, changedIndex);

  if (!selectedVariant) {
    card.classList.add('has-error');

    const error = card.querySelector('[data-figs-card-error]');

    if (error) {
      error.textContent =
        'Cette combinaison est indisponible. Choisissez une autre option.';
    }

    const addButton = card.querySelector(
      '[data-figs-config] [data-figs-quick-add]'
    );

    if (addButton) {
      addButton.disabled = true;
    }

    return;
  }

  card.classList.remove('has-error');
  applyFigsVariantToCard(card, selectedVariant);
}

function closeFigsRecommendationCard(card) {
  if (!card) return;

  const panel = card.querySelector('[data-figs-config]');
  const chooseButton = card.querySelector('[data-figs-configure]');

  if (panel) {
    panel.hidden = true;
  }

  if (chooseButton) {
    chooseButton.setAttribute('aria-expanded', 'false');
  }

  card.classList.remove('is-open');
}

function openFigsRecommendationCard(card) {
  if (!card) return;

  document
    .querySelectorAll(`${FIGS_SMART_CARD_SELECTOR}.is-open`)
    .forEach((otherCard) => {
      if (otherCard !== card) {
        closeFigsRecommendationCard(otherCard);
      }
    });

  const panel = card.querySelector('[data-figs-config]');
  const chooseButton = card.querySelector('[data-figs-configure]');

  if (!panel) return;

  panel.hidden = false;
  card.classList.add('is-open');

  if (chooseButton) {
    chooseButton.setAttribute('aria-expanded', 'true');
  }

  syncFigsRecommendationCard(card);

  window.requestAnimationFrame(() => {
    panel
      .querySelector('[data-figs-option-select]')
      ?.focus({ preventScroll: true });
  });
}

function initializeFigsSmartCart(root = document) {
  root.querySelectorAll(FIGS_SMART_CARD_SELECTOR).forEach((card) => {
    if (!card.dataset.figsSmartInitialized) {
      card.dataset.figsSmartInitialized = 'true';

      getFigsOptionSelects(card).forEach((select) => {
        select.addEventListener('change', (event) => {
          /*
           * Stops Shopify's cart quantity validator from treating
           * this product-option selector as a quantity input.
           */
          event.stopPropagation();

          const optionIndex = Number(select.dataset.optionIndex || 0);

          card.classList.remove('has-error');
          syncFigsRecommendationCard(card, optionIndex);
        });
      });
    }

    syncFigsRecommendationCard(card);
  });

  initializeFigsRecommendationCarousels(root);
}


function getFigsCarouselIndex(carousel) {
  if (!carousel) return 0;

  const cards = Array.from(
    carousel.querySelectorAll(FIGS_SMART_CARD_SELECTOR)
  );

  if (!cards.length) return 0;

  const carouselCenter = carousel.scrollLeft + (carousel.clientWidth / 2);

  return cards
    .map((card, index) => ({
      index,
      distance: Math.abs(
        (card.offsetLeft + (card.offsetWidth / 2)) - carouselCenter
      ),
    }))
    .sort((first, second) => first.distance - second.distance)[0].index;
}

function updateFigsCarouselControls(shell) {
  if (!shell) return;

  const carousel = shell.querySelector('[data-figs-recommendation-carousel]');
  const previousButton = shell.querySelector('[data-figs-carousel-prev]');
  const nextButton = shell.querySelector('[data-figs-carousel-next]');
  const section = shell.closest('.figs-smart-recs');
  const dots = Array.from(
    section?.querySelectorAll('.figs-smart-recs__dot') || []
  );
  const cards = Array.from(
    carousel?.querySelectorAll(FIGS_SMART_CARD_SELECTOR) || []
  );

  if (!carousel || !cards.length) return;

  const activeIndex = getFigsCarouselIndex(carousel);
  const hasOverflow = carousel.scrollWidth > carousel.clientWidth + 2;

  if (previousButton) {
    previousButton.hidden = cards.length < 2;
    previousButton.disabled = !hasOverflow || activeIndex <= 0;
  }

  if (nextButton) {
    nextButton.hidden = cards.length < 2;
    nextButton.disabled = !hasOverflow || activeIndex >= cards.length - 1;
  }

  dots.forEach((dot, index) => {
    dot.classList.toggle('is-active', index === activeIndex);
  });
}

function scrollFigsCarousel(shell, direction) {
  if (!shell) return;

  const carousel = shell.querySelector('[data-figs-recommendation-carousel]');
  const cards = Array.from(
    carousel?.querySelectorAll(FIGS_SMART_CARD_SELECTOR) || []
  );

  if (!carousel || cards.length < 2) return;

  const currentIndex = getFigsCarouselIndex(carousel);
  const targetIndex = Math.max(
    0,
    Math.min(cards.length - 1, currentIndex + direction)
  );

  carousel.scrollTo({
    left: cards[targetIndex].offsetLeft,
    behavior: 'smooth',
  });
}

function initializeFigsRecommendationCarousels(root = document) {
  root.querySelectorAll('[data-figs-carousel-shell]').forEach((shell) => {
    if (!shell.dataset.figsCarouselInitialized) {
      shell.dataset.figsCarouselInitialized = 'true';

      const carousel = shell.querySelector(
        '[data-figs-recommendation-carousel]'
      );

      if (carousel) {
        let scrollTimer;

        carousel.addEventListener(
          'scroll',
          () => {
            window.clearTimeout(scrollTimer);
            scrollTimer = window.setTimeout(
              () => updateFigsCarouselControls(shell),
              60
            );
          },
          { passive: true }
        );
      }
    }

    updateFigsCarouselControls(shell);
  });
}

async function addFigsRecommendation(button) {
  const card = button.closest(FIGS_SMART_CARD_SELECTOR);
  const cartDrawer = document.querySelector('cart-drawer');
  const errorMessage = card?.querySelector('[data-figs-card-error]');

  const variantId = Number(
    button.dataset.variantId ||
    card?.dataset.selectedVariantId
  );

  if (
    !card ||
    !cartDrawer ||
    !Number.isFinite(variantId) ||
    variantId <= 0
  ) {
    if (card) {
      card.classList.add('has-error');
    }

    if (errorMessage) {
      errorMessage.textContent =
        'Choisissez une couleur et une taille disponibles.';
    }

    return;
  }

  const defaultLabel = button.dataset.defaultLabel || 'Ajouter';

  card.classList.remove('has-error');

  if (errorMessage) {
    errorMessage.textContent =
      'Impossible d’ajouter cet article. Réessayez.';
  }

  button.disabled = true;
  button.textContent = 'Ajout…';

  try {
    const sectionIds = cartDrawer
      .getSectionsToRender()
      .map((section) => section.id);

    const response = await fetch(
      window.routes?.cart_add_url || '/cart/add.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      }
    );

    let parsedState;

    try {
      parsedState = await response.json();
    } catch (parseError) {
      throw new Error(
        'Réponse Shopify invalide. Rechargez la page et réessayez.'
      );
    }

    if (!response.ok || parsedState.status) {
      throw new Error(
        parsedState.description ||
        parsedState.message ||
        'Impossible d’ajouter cet article.'
      );
    }

    button.textContent = 'Ajouté ✓';
    cartDrawer.renderContents(parsedState);
  } catch (errorObject) {
    console.error('FIGS Smart Cart:', errorObject);

    card.classList.add('has-error');

    if (errorMessage) {
      errorMessage.textContent =
        errorObject.message ||
        'Impossible d’ajouter cet article. Réessayez.';
    }

    button.disabled = false;
    button.textContent = 'Réessayer';

    window.setTimeout(() => {
      if (document.body.contains(button)) {
        button.textContent = defaultLabel;
        syncFigsRecommendationCard(card);
      }
    }, 1800);
  }
}

document.addEventListener('click', (event) => {
  const previousButton = event.target.closest('[data-figs-carousel-prev]');

  if (previousButton) {
    event.preventDefault();
    event.stopPropagation();

    scrollFigsCarousel(
      previousButton.closest('[data-figs-carousel-shell]'),
      -1
    );

    return;
  }

  const nextButton = event.target.closest('[data-figs-carousel-next]');

  if (nextButton) {
    event.preventDefault();
    event.stopPropagation();

    scrollFigsCarousel(
      nextButton.closest('[data-figs-carousel-shell]'),
      1
    );

    return;
  }

  const chooseButton = event.target.closest('[data-figs-configure]');

  if (chooseButton) {
    event.preventDefault();
    event.stopPropagation();

    openFigsRecommendationCard(
      chooseButton.closest(FIGS_SMART_CARD_SELECTOR)
    );

    return;
  }

  const cancelButton = event.target.closest('[data-figs-config-cancel]');

  if (cancelButton) {
    event.preventDefault();
    event.stopPropagation();

    closeFigsRecommendationCard(
      cancelButton.closest(FIGS_SMART_CARD_SELECTOR)
    );

    return;
  }

  const addButton = event.target.closest('[data-figs-quick-add]');

  if (!addButton) return;

  event.preventDefault();
  event.stopPropagation();

  addFigsRecommendation(addButton);
});


window.addEventListener(
  'resize',
  () => initializeFigsRecommendationCarousels(document),
  { passive: true }
);

const figsSmartCartObserver = new MutationObserver((mutations) => {
  const shouldInitialize = mutations.some((mutation) =>
    Array.from(mutation.addedNodes).some((node) => {
      if (node.nodeType !== 1) {
        return false;
      }

      return Boolean(
        node.matches?.(FIGS_SMART_CARD_SELECTOR) ||
        node.querySelector?.(FIGS_SMART_CARD_SELECTOR) ||
        node.matches?.('[data-figs-carousel-shell]') ||
        node.querySelector?.('[data-figs-carousel-shell]')
      );
    })
  );

  if (shouldInitialize) {
    initializeFigsSmartCart(document);
  }
});

figsSmartCartObserver.observe(document.body, {
  childList: true,
  subtree: true,
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeFigsSmartCart(document);
  });
} else {
  initializeFigsSmartCart(document);
}
